import { defineStore } from 'pinia'
import { ref, Ref, computed } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { instance, getApiPrefix, FileType } from '@cognitivecredit/ui-utils'
import { syndtrakDisplayPath, syndtrakFolderPath } from '@/utils/dealConnect'
import { getApiDomain } from '@/config'
import store from '@/stores/index'
import {
  DealConnectDeal,
  DealConnectDealsResponse,
  DealConnectDocument,
  DealConnectDocumentsResponse,
  DealConnectFile,
  DealConnectFolderNote,
} from '@/components/dealconnect/types'

dayjs.extend(utc)
dayjs.extend(timezone)

const getReq = async () => {
  const prefix = getApiPrefix('rac')
  return instance({ prefix, apiDomain: getApiDomain(prefix) })
}

const bookmarkProvider = (deal: DealConnectDeal) =>
  deal.source === 'managed_access' ? 'managed_access' : deal.datasite

const decodeDisplayPath = (filePath: string, source: string): string => {
  if (source !== 'managed_access') {
    return filePath
  }
  try {
    return decodeURIComponent(decodeURIComponent(filePath))
  } catch {
    return filePath
  }
}

// Convert a UTC timestamp into a naive local-time string in the user's preferred
// timezone so that downstream `medDate` formatting (which uses browser TZ) shows
// wall-clock time in that timezone instead of the browser's.
const shiftUpdatedAtToUserTimezone = (updatedAt: string | null): string => {
  if (!updatedAt) {
    return ''
  }
  const userTimezone = store.getters['User/preferences']?.timezone
  if (!userTimezone) {
    return updatedAt
  }
  const parsed = dayjs(updatedAt)
  if (!parsed.isValid()) {
    return updatedAt
  }
  return parsed.tz(userTimezone).format('YYYY-MM-DD[T]HH:mm:ss')
}

const normalizeFolderNotes = (notes: DealConnectFolderNote[] | undefined, dealId: string): DealConnectFolderNote[] =>
  (notes ?? []).flatMap((item) => {
    const path = syndtrakFolderPath(item.path ?? '')
    const note = item.note?.trim() ?? ''
    if (item.dealId !== dealId || !path || !note) {
      return []
    }
    return [{ dealId, path, note }]
  })

const toPrivateDocumentLabel = (value: boolean | null): 'private' | 'public' | null => {
  if (value === true) {
    return 'private'
  }
  if (value === false) {
    return 'public'
  }
  return null
}

const toFile = (doc: DealConnectDocument, dealId: string, dealDatasite?: string): DealConnectFile => {
  const datasite = doc.datasite || dealDatasite || ''
  const displayPath = syndtrakDisplayPath(decodeDisplayPath(doc.filePath, doc.source), datasite)
  const fileName = displayPath.split('/').pop() ?? displayPath
  const ext = fileName.split('.').pop()?.toLowerCase() ?? 'file'
  return {
    path: displayPath,
    name: fileName,
    filePath: doc.filePath,
    modifiedAt: doc.updatedAt ? shiftUpdatedAtToUserTimezone(doc.updatedAt) : '',
    type: ext as FileType,
    dealSiteId: doc.dealId || dealId,
    clientId: doc.clientId,
    source: doc.source,
    documentId: doc.documentId,
    privateDocument: toPrivateDocumentLabel(doc.privateDocument),
    datasite,
    downloadUrl: doc.downloadUrl || null,
    lastAccessedAt: doc.lastAccessedAt ? shiftUpdatedAtToUserTimezone(doc.lastAccessedAt) : null,
    note: doc.note?.trim() || null,
    dealName: doc.dealName ?? null,
    companyId: doc.companyId ?? null,
  }
}

const documentsForDeal = (body: DealConnectDocumentsResponse, dealId: string, dealDatasite?: string) => ({
  files: body.documents.map((doc) => toFile(doc, dealId, dealDatasite)),
  folderNotes: normalizeFolderNotes(body.folderNotes, dealId),
})

export const useDealConnectStore = defineStore('dealConnect', () => {
  const deals: Ref<DealConnectDeal[]> = ref([])
  const selectedDeal: Ref<DealConnectDeal | null> = ref(null)
  const documents: Ref<DealConnectFile[]> = ref([])
  const folderNotes: Ref<DealConnectFolderNote[]> = ref([])
  const newDocuments: Ref<DealConnectFile[]> = ref([])
  const loading: Ref<boolean> = ref(false)
  const documentsLoading: Ref<boolean> = ref(false)
  const newDocumentsLoading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)
  const excludeDealsWithoutCompany: Ref<boolean> = ref(false)
  const pendingBookmarks: Ref<Record<string, true>> = ref({})
  const viewAsClientId = ref('')
  let clientContext = 0
  let pendingDeals: Promise<boolean> | null = null

  const get = async (path: string, params?: Record<string, string>) => {
    const req = await getReq()
    if (params) {
      return req.get(path, { params })
    }
    return req.get(path)
  }

  const withViewAsClient = (params?: Record<string, string>) =>
    viewAsClientId.value ? { ...params, clientId: viewAsClientId.value } : params

  const setViewAsClient = (clientId: string) => {
    if (viewAsClientId.value === clientId) {
      return
    }
    viewAsClientId.value = clientId
    clientContext++
    pendingDeals = null
    deals.value = []
    selectedDeal.value = null
    documents.value = []
    folderNotes.value = []
    newDocuments.value = []
    loading.value = false
    documentsLoading.value = false
    newDocumentsLoading.value = false
    error.value = null
  }

  // Company-scoped state (for individual company deals page)
  const companyDocuments: Ref<DealConnectFile[]> = ref([])
  const companyFolderNotes: Ref<DealConnectFolderNote[]> = ref([])
  const companyDocumentsLoading: Ref<boolean> = ref(false)
  const companyDealsError: Ref<string | null> = ref(null)

  const requestDeals = async (): Promise<boolean> => {
    const context = clientContext
    // Later navigations refetch in the background. Toggling loading would unmount the
    // explorer table and drop search, sort, and scroll.
    const initialLoad = deals.value.length === 0
    if (initialLoad) {
      loading.value = true
    }
    error.value = null
    try {
      const res = await get('rac/dealconnect/deals', withViewAsClient())
      if (context !== clientContext) {
        return false
      }
      const body = res.data as DealConnectDealsResponse
      deals.value = body.deals
      return true
    } catch (e) {
      if (context !== clientContext) {
        return false
      }
      const err = e as Error
      console.error('[ERROR] Fetching dealconnect deals', err)
      error.value = 'Failed to load deals'
      return false
    } finally {
      if (initialLoad && context === clientContext) {
        loading.value = false
      }
    }
  }

  const fetchDeals = (): Promise<boolean> => {
    if (pendingDeals) {
      return pendingDeals
    }
    const pending = requestDeals().finally(() => {
      if (pendingDeals === pending) {
        pendingDeals = null
      }
    })
    pendingDeals = pending
    return pending
  }

  const dealsLength = computed(() => deals.value.length)

  const fetchDocuments = async (dealId: string) => {
    const context = clientContext
    documentsLoading.value = true
    try {
      error.value = null
      const res = await get('rac/dealconnect/documents', withViewAsClient({ dealId }))
      if (context !== clientContext) {
        return
      }
      const body = res.data as DealConnectDocumentsResponse
      const dealDatasite =
        deals.value.find((deal) => deal.id === dealId)?.datasite ??
        (selectedDeal.value?.id === dealId ? selectedDeal.value.datasite : undefined)
      const dealDocuments = documentsForDeal(body, dealId, dealDatasite)
      documents.value = dealDocuments.files
      folderNotes.value = dealDocuments.folderNotes
    } catch (e) {
      if (context !== clientContext) {
        return
      }
      const err = e as Error
      console.error('[ERROR] Fetching dealconnect documents', err)
      error.value = 'Failed to load documents'
    } finally {
      if (context === clientContext) {
        documentsLoading.value = false
      }
    }
  }

  let newDocumentsRequest = 0

  const fetchNewDocuments = async () => {
    const context = clientContext
    const request = ++newDocumentsRequest
    newDocumentsLoading.value = true
    try {
      error.value = null
      const res = await get('rac/dealconnect/documents')
      if (request !== newDocumentsRequest || context !== clientContext) {
        return
      }
      const body = res.data as DealConnectDocumentsResponse
      newDocuments.value = body.documents.map((doc) => toFile(doc, doc.dealId))
    } catch (e) {
      if (request !== newDocumentsRequest || context !== clientContext) {
        return
      }
      const err = e as Error
      console.error('[ERROR] Fetching new documents', err)
      error.value = 'Failed to load documents'
    } finally {
      if (request === newDocumentsRequest && context === clientContext) {
        newDocumentsLoading.value = false
      }
    }
  }

  const selectDeal = (deal: DealConnectDeal | null) => {
    const current = selectedDeal.value
    // The click selects the deal immediately, then the route watch selects it again.
    if (deal && current?.id === deal.id && current.datasite.toLowerCase() === deal.datasite.toLowerCase()) {
      selectedDeal.value = deal
      return
    }
    selectedDeal.value = deal
    if (deal) {
      fetchDocuments(deal.id)
    } else {
      documents.value = []
      folderNotes.value = []
    }
  }

  const getSignedDownloadUrl = async (path: string | null | undefined): Promise<string> => {
    if (!path) {
      throw new Error('Download URL is not available')
    }
    const res = await get(path)
    return res.data.url
  }

  // hacky implementation: we get sent a preformatted download URL,
  // but the endpoint ending in /download actually downloads the file
  // whereas the endpoint without /download allows us to view the
  // file in the browser 🤷
  const getSignedViewUrl = async (path: string | null | undefined): Promise<string> => {
    if (!path) {
      throw new Error('Download URL is not available')
    }
    const res = await get(path.split('/download')[0])
    return res.data.url
  }

  const getManagedAccessDownloadUrl = async (dealId: string, clientId: string, filePath: string): Promise<string> => {
    const res = await get(`rac/clients/${clientId}/deals/${dealId}/${filePath}`)
    return res.request.responseURL
  }

  const getManagedAccessBulkDownloadUrls = async (clientId: string, companyId: string): Promise<string[]> => {
    const res = await get(`rac/clients/${clientId}/companies/${companyId}/deals/download`)
    const { downloadUrls } = res.data
    return downloadUrls.map((item: { downloadUrl: string }) => item.downloadUrl)
  }

  const getManagedAccessZipDownloadUrl = async (
    clientId: string,
    companyId: string,
    dealId: string,
  ): Promise<string> => {
    const res = await get(`rac/clients/${clientId}/companies/${companyId}/deals/download`)
    const { downloadUrls } = res.data
    const match = downloadUrls.find((item: { dealId: string; downloadUrl: string }) => item.dealId === dealId)
    if (!match) {
      throw new Error(`No managed access download URL found for deal ${dealId}`)
    }
    return match.downloadUrl
  }

  const dismissError = () => {
    error.value = null
  }

  // Mark a document as accessed by updating the lastAccessedAt timestamp
  // so no need to refetch the documents from the server
  const markDocumentAccessed = (file: DealConnectFile) => {
    const accessedAt = shiftUpdatedAtToUserTimezone(new Date().toISOString())
    const stamp = (files: DealConnectFile[]) =>
      files.map((doc) =>
        doc.dealSiteId === file.dealSiteId && doc.documentId === file.documentId && doc.filePath === file.filePath
          ? { ...doc, lastAccessedAt: accessedAt }
          : doc,
      )
    documents.value = stamp(documents.value)
    companyDocuments.value = stamp(companyDocuments.value)
    newDocuments.value = stamp(newDocuments.value)
  }

  const bookmarkKey = (deal: DealConnectDeal) => `${bookmarkProvider(deal)}:${deal.id}`

  const isBookmarkPending = (deal: DealConnectDeal) => !!pendingBookmarks.value[bookmarkKey(deal)]

  const toggleBookmark = async (deal: DealConnectDeal) => {
    const key = bookmarkKey(deal)
    if (pendingBookmarks.value[key]) {
      return
    }
    const provider = bookmarkProvider(deal)
    const next = !deal.bookmarked
    pendingBookmarks.value = { ...pendingBookmarks.value, [key]: true }
    error.value = null
    try {
      const req = await getReq()
      if (deal.bookmarked) {
        await req.delete(`rac/dealconnect/bookmarks/${encodeURIComponent(provider)}/${encodeURIComponent(deal.id)}`)
      } else {
        await req.put('rac/dealconnect/bookmarks', { provider, dealId: deal.id })
      }
      deal.bookmarked = next
      const listed = deals.value.find((item) => item.id === deal.id)
      if (listed && listed !== deal) {
        listed.bookmarked = next
      }
      if (selectedDeal.value && selectedDeal.value !== deal && selectedDeal.value.id === deal.id) {
        selectedDeal.value.bookmarked = next
      }
    } catch (e) {
      const err = e as Error
      console.error('[ERROR] Updating deal bookmark', err)
      error.value = 'Failed to update bookmark'
    } finally {
      const rest = { ...pendingBookmarks.value }
      delete rest[key]
      pendingBookmarks.value = rest
    }
  }

  const getDealsForCompany = (companyId: string): DealConnectDeal[] => {
    return deals.value.filter((deal) => deal.companyId?.includes(companyId))
  }

  const fetchDocumentsForCompany = async (companyId: string) => {
    companyDocumentsLoading.value = true
    companyDealsError.value = null
    try {
      if (!deals.value.length) {
        await fetchDeals()
      }
      const companyDeals = getDealsForCompany(companyId)
      const req = await getReq()
      const allDocs = await Promise.all(
        companyDeals.map(async (deal) => {
          const res = await req.get('rac/dealconnect/documents', { params: { dealId: deal.id } })
          const body = res.data as DealConnectDocumentsResponse
          return documentsForDeal(body, deal.id, deal.datasite)
        }),
      )
      companyDocuments.value = allDocs.flatMap((item) => item.files)
      companyFolderNotes.value = allDocs.flatMap((item) => item.folderNotes)
    } catch (e) {
      const err = e as Error
      console.error('[ERROR] Fetching dealconnect documents for company', err)
      companyDealsError.value = 'Failed to load deal documents'
    } finally {
      companyDocumentsLoading.value = false
    }
  }

  return {
    viewAsClientId,
    setViewAsClient,
    deals,
    selectedDeal,
    documents,
    folderNotes,
    newDocuments,
    loading,
    documentsLoading,
    newDocumentsLoading,
    dealsLength,
    error,
    fetchDeals,
    fetchDocuments,
    fetchNewDocuments,
    selectDeal,
    getSignedDownloadUrl,
    getSignedViewUrl,
    getManagedAccessDownloadUrl,
    getManagedAccessBulkDownloadUrls,
    getManagedAccessZipDownloadUrl,
    dismissError,
    markDocumentAccessed,
    toggleBookmark,
    isBookmarkPending,
    excludeDealsWithoutCompany,
    companyDocuments,
    companyFolderNotes,
    companyDocumentsLoading,
    companyDealsError,
    getDealsForCompany,
    fetchDocumentsForCompany,
  }
})
